import Backbone from 'backbone'
import _ from 'lodash'
import DatasetsNumericFilter from 'scoreboard/datasets/filters/datasets-numeric-filter'
import DatasetsTextFilter from 'scoreboard/datasets/filters/datasets-text-filter'
import DatasetsDateFilterStep1 from 'scoreboard/datasets/filters/datasets-date-filter-step1'
import DatasetsRollupFilterStep1 from 'scoreboard/datasets/filters/datasets-rollup-filter-step1'
import template from './transformation-if.hbs'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import i18n from 'scoreboard/components/i18n'
import twoSteps from 'scoreboard/datasets/filters/two-steps'
import DatasetsDateFilterStep2 from 'scoreboard/datasets/filters/datasets-date-filter-step2'
import DatasetsTimeFilterStep2 from 'scoreboard/datasets/filters/datasets-time-filter-step2'
import DatasetsDateTimeFilterStep2 from 'scoreboard/datasets/filters/datasets-date-time-filter-step2'
import appDisplay from 'scoreboard/components/app-display'
import formHelper from 'scoreboard/components/form-helper'
import filterText from 'scoreboard/datasets/filters/filter-text'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import MarkedField from 'scoreboard/components/marked-field'
import ifOperators from './if-operators'
import selects from 'scoreboard/components/selects'
import renderValuesSelect from 'scoreboard/datasets/filters/render-values-select'
import EditableButton from 'scoreboard/components/editable-button'
import positiveIntegerValidator from 'scoreboard/components/validators/positive-integer-validator'
import ifOperatorOptions from './if-operator-options'
import formatValue from 'scoreboard/components/format-value'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER, TEXT, YES_NO, DATE, TIME, DATE_TIME } = DataType
import renderStrongOperatorDropdown from 'scoreboard/datasets/filters/render-strong-operator-dropdown'
import DatasetsRollupFilterStep2 from 'scoreboard/datasets/filters/datasets-rollup-filter-step2'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'
import deleteNonApplicableFilterProperties from 'scoreboard/datasets/filters/delete-non-applicable-filter-properties'
import Dataset from 'scoreboard/datasets/model/dataset'
const { BLANK } = Dataset
import { convertPercentValuesForValuesSelect } from 'scoreboard/imports/transform/value-converter-for-values-select'

const valuesOperators = ['is-any', 'is-not-any', 'contains-all',
	'contains-any', 'does-not-contain-all', 'does-not-contain-any',
	'character-length-is-any', 'character-length-is-not-any']
const yesNoValuesOperators = ['contains-all', 'contains-any',
	'does-not-contain-all', 'does-not-contain-any',
	'character-length-is-any', 'character-length-is-not-any']

var TransformationIf = Backbone.View.extend({

	// #52951 do not apply `draggable-list-nodrag` at this level
	className: 'transformation-if',

	/**
	 * The "if" portion of a transformation
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Importable} options.model
	 *
	 * @param {FormView} options.formView
	 *
	 * @param {Array} options.fields the fields which must have id and label
	 * properties, and may optionally have a dataTypes property. The id property
	 * can be a String if needed (e.g. for datasets)
	 *
	 * @param {Object} [options.transformation] the transformation currently
	 * being edited
	 *
	 * @param {Boolean} [options.stronglyTyped=false] indicates the operators
	 * for the filter should be determined by the data type of the field
	 * (see #52694)
	 *
	 * @param {Object} [options.treeItemsById] tree items keyed by ID
	 *
	 * @param {Map<String, Object>} [options.formattingOptionsByFieldRawColumn]
	 * formatting options for basic fields, keyed by the field's raw column name
	 */
	initialize: function(options = {}) {
		this.options = options
		this.formView = options.formView
		// by label since we won't always have an ID (e.g. when working with
		// datasets)
		this.fieldsById = _.keyBy(this.options.fields, 'id')
		// Holds any state specified in a second-level dialog (launched by
		// the "Choose" button)
		this.if = _.get(this.options.transformation, 'if')
		// original field id, if any
		this.oldFieldId = _.get(this.if, 'fieldId')
		this.stronglyTyped = options.stronglyTyped ?? false
		this.treeItemsById = options.treeItemsById ?? {}
		this.formattingOptionsByFieldRawColumn = options.formattingOptionsByFieldRawColumn ?? {}
	},

	events: {
		'change [name=fieldId]': 'onChangeField',
		'change [name=operator]': 'onChangeOperator',
		'click .choose-criteria': 'chooseCriteria',
		'change select[name=values]' : 'onChangeValues',
		'input [name=value1]': 'onChangeValue',
		'input [name=value2]': 'onChangeValue'
	},

	render: function() {
		this.$el.html(template())

		// render field select
		selects.filtering(this.$('[name=fieldId]'), {
			options: this.options.fields,
			accessors: { value: 'id' },
			groupBy: this.options.groupBy
		})
		// mark field select as required
		this.fieldField = new MarkedField({
			el: this.$('[name=fieldId]'),
			validators: [requiredValidator]
		}).render()

		this.renderOperatorSelect()

		// This is a form, and we do need to write some values to the DOM, but
		// we don't want to write data to ALL input elements. Calling that
		// function on all inputs messes up multi-filter selects which have already been set
		// (https://github.com/SpiderStrategies/Scoreboard/issues/29738) and if
		// the function is called before the selects are created, we run into other problems
		// (https://github.com/SpiderStrategies/Scoreboard/issues/29345). Exclude data
		// for those selects in order to skip those elements in formHelper.writeToDom()
		let data = _.get(this.options.transformation, 'if')
		let dataToWrite = _.omit(data, 'values', 'months', 'periodIds', 'days', 'dates')
		formHelper.writeToDom(dataToWrite, this.el)

		// after writeToDom
		this.renderChooseCriteriaButton()

		// #40586 call onChangeOperator after writeToDom, to allow correct
		// formatting and markerContent for numericFilter marked fields
		this.onChangeOperator()

		this.toggleCriteriaVisibility()

		return this
	},

	renderOperatorSelect: function() {
		let operator = _.get(this.options.transformation, 'if.operator')
		let operatorEl = this.$('[name=operator]')
		let options
		if (this.stronglyTyped) {
			let fieldId = this.$('[name=fieldId]').val()
			if (_.isEmpty(fieldId)) {
				fieldId = this.options.transformation?.if?.fieldId
			}
			let filterTypeEl = this.$('.filter-type')
			if (_.isNil(fieldId) || '' === fieldId) {
				filterTypeEl.addClass('is-not-displayed')
			} else {
				filterTypeEl.removeClass('is-not-displayed')

				renderStrongOperatorDropdown({
					el: operatorEl,
					parentDomainType: 'forms',
					filter: this.readFromDom(),
					underlyingField: this.model.getUnderlyingField(fieldId),
					// I'm too short on time to allow this right now
					allowEqualsEquation: false,
					ellipsis: false // #52910
				})
			}
		} else {
			// ordered if operators with headings
			options = ifOperatorOptions()
			selects(operatorEl.empty(), {
				options,
				accessors: {value: 'id'},
				groupBy: 'group',
				selected: operator,
				sort: [] // maintain order set in ifOperatorOptions
			})
		}
		if (!this.operatorField) {
			this.operatorField = new MarkedField({
				el: operatorEl,
				validator: requiredValidator
			}).render()
		}
	},

	/**
	 * Reset the choose criteria button to "Choose..."
	 * to let the user know to make date selections after changing to a new
	 * date operators
	 */
	resetChooseCriteriaButton: function() {
		let operator = this.getOperator()
		let oldOperator = this.oldOperator
		// if this is a change to a different operator
		if (!_.isNil(oldOperator) && operator !== oldOperator) {
			// clear the choose criteria button
			this.chooseCriteriaButton && this.chooseCriteriaButton.renderLabel()
		}
	},

	renderChooseCriteriaButton: function() {
		this.chooseCriteriaButton = new EditableButton({
			el: this.$('.choose-criteria'),
			message: 'common.choose',
			label: this.getCriteriaLabel(),
			escapeLabel: false
		}).render()
	},

	getCriteriaLabel: function() {
		// if we have an existing "if" clause, set the label
		if (this.if) {
			let operator = this.getOperator()

			// as of 5/2/19 (3.4), when weakly typed, only date fields have a second step
			let dataType = this.stronglyTyped ? this.getDataType() : DATE

			if (!twoSteps(dataType, operator)) {
				return
			}
			// #55152 make sure the field is retrieved whether this is from
			// datasets or forms
			const field = this.model?.getUnderlyingField?.(this.getFieldId()) ??
				this.fieldsById[this.getFieldId()] ?? { dataType }
			return filterText.noOperator(this.model, this.readFromDom(), field,
				this.treeItemsById, { showCalendarLabel: true })
		}

	},

	renderValuesSelect: function (values) {
		let operator = this.getOperator()
		// #30824 add labels to values select
		this.$('.values-select').removeClass('is-not-displayed')
		let valuesSelectLabel = i18n.get('admin.import.transform.if.values')
		if (this.characterLengthOperator()) {
			valuesSelectLabel = i18n.get('admin.import.transform.then.numbers')
		}
		if (this.getDataType() === TEXT) {
			valuesSelectLabel = i18n.get('admin.import.transform.header.text')
		}
		this.$('.values-select .label-text').html(valuesSelectLabel)
		let dataType = this.getFieldDataType(this.getFieldId())

		// #53052 if the dataType is date/time, and the filter is not strongly
		// typed, use the text dataType since we are matching on raw values
		if (_.includes([DATE, DATE_TIME, TIME], dataType)
			&& !this.stronglyTyped) dataType = TEXT

		// #53065 provide the currency code to render-values-select
		let field = this.fieldsById[this.getFieldId()]
		// #53703 also provide precision (will be available for
		// transformation-if used from forms logician)
		let currencyCode = field?.currencyCode
		let precision = field?.precision
		// #53703 for transformations from datasets-edit, the "field"
		// will not have currencyCode and precision populated,
		// but we can see if that information is provided
		// from this.formattingOptionsByFieldRawColumn
		if (!_.isEmpty(this.formattingOptionsByFieldRawColumn)) {
			// "this.fieldId" may be a field label, not an id
			let formattingOptions =
				this.formattingOptionsByFieldRawColumn?.get(this.getFieldId())
			if (_.isUndefined(currencyCode)) {
				currencyCode = formattingOptions?.currencyCode
			}
			if (_.isUndefined(precision)) {
				precision = formattingOptions?.precision
			}
			if (!_.isUndefined(formattingOptions?.dataType)) {
				dataType = formattingOptions?.dataType
			}
		}

		// #55026 #55027 if this is a yes-no field and the operator is one that
		// should allow entering text values, pass in a dataType of text for
		// the values select to allow entering any value (and not just the
		// values normally allowed for yes-no fields - see render-yes-no-select.js)
		if (dataType == YES_NO && this.isYesNoValuesOperator()) {
			dataType = TEXT
		}
		renderValuesSelect({
			dataset: this.model,
			fieldId: this.getFieldId(),
			el: this.$('[name=values]'),
			operator,
			suggestions: this.getSuggestionsForField(this.getFieldId()),
			values,
			dataType,
			precision,
			currencyCode,
			valueConverter: this._convertValue.bind(this)
		})
	},

	/**
	 * Passed to render-values-select
	 * This ensures that newly-entered values for fields with dataType of PERCENT
	 * have a "create new" item label that reflects what was entered
	 * (ex. typing in 33 should display as "+ Add 33%" instead of "+ Add 3300%")
	 * #51132
	 **/
	_convertValue(value) {
		let dataType = this.getFieldDataType(this.getFieldId())
		return convertPercentValuesForValuesSelect(dataType, value)
	},

	/**
	 * @abstract
	 */
	getSuggestionsForField: function(fieldId) { return [] },

	onChangeField: function() {
		// #32975 only update the operator select if we are
		// switching to or from the fieldId of 'all-records'
		let fieldId = this.getFieldId()
		let oldFieldId = this.oldFieldId
		if (this.isAllRecords() || oldFieldId === 'all-records' ||
			this.stronglyTyped) {
			this.renderOperatorSelect()
			this.onChangeOperator()
			this.toggleCriteriaVisibility()
		}
		// only clear the values if the dataType changed
		const currentDataType = this.getFieldDataType(this.getFieldId())
		const oldDataType = this.getFieldDataType(oldFieldId)
		if (currentDataType !== oldDataType) {
			// #53240 clear values and equations if the dataType changed as
			// those fields have different roles depending on the data type
			if (this.if) {
				this.if.values = []
				this.if.equations = []
				this.if.dataType = currentDataType
				deleteNonApplicableFilterProperties(this.if, currentDataType)
			}

			// #40586 call onChangeOperator to make sure we have correct
			// formatting and markerContent for numericFilter marked fields
			this.onChangeOperator()
		}
		// re-render the values select so suggestions are up to date with the field selected
		this.changeValuesOperator()

		// reset the label and the rollupIsAnyFilters if we switch the field
		if (this.if) this.if.rollupIsAnyFilters = {}
		this.chooseCriteriaButton?.renderLabel(this.getCriteriaLabel())

		this.oldFieldId = fieldId
		this.trigger('change')
	},

	onChangeValue: function() {
		this.trigger('change')
	},

	onChangeOperator: function() {
		let operator = this.getOperator()
		let dataType = this.getDataType()
		// #53065 provide the selected field for any of the filters
		// that may need it (ex datasets-date-filter-step1 needs the field's
		// time display option, if it is different from the default)
		let fieldId = this.getFieldId()
		let field = this.fieldsById[fieldId]

		_.invokeMap(this.filterViews(), 'cleanup')
		// #52868 only show controls for one data type at a time
		this.$('[data-type]').addClass('is-not-displayed')

		let options = _.extend({}, this.options, {
			el: this.el,
			formView: this.formView,
			allowIncomplete: this.options.allowIncomplete,
			filter: _.get(this.options.transformation, 'if', {}),
			dataType,
			field
		})

		delete this.textFilter
		delete this.numericFilter
		delete this.dateFilter1
		delete this.rollupFilter

		if (isNumericDataType(dataType)) {
			if (this.characterLengthOperator()) {
				options.validator = positiveIntegerValidator
			}
			// #40586 if we have a dataType for the field, use that as the
			// dataType for the numericFilter (value imports will  not have this)
			// #46808 only needed if the field data type is also numeric
			let fieldDataType = this.getFieldDataType(this.getFieldId())
			if (!_.isNil(fieldDataType) && isNumericDataType(fieldDataType)) {
				options.dataType = fieldDataType
			}
			this.numericFilter = new DatasetsNumericFilter(options).render()
			this.numericFilter.on('change-values-operator', this.changeValuesOperator.bind(this))
			// #30824 add label text to DatasetsNumericFilter value labels
			// the label text div is only inserted for transformations,
			// so we don't mess up the layout of dataset filters
			let valueLabel = i18n.get('admin.import.transform.label.number')
			let html = `<div class="label-text">${valueLabel}</div>`
			this.$('.value-label').prepend(html)
		}
		if (dataType === TEXT) {
			this.textFilter = new DatasetsTextFilter(options).render()
			this.textFilter.on('change-values-operator', this.changeValuesOperator.bind(this))
			// #30824 add label text to DatasetsTextFilter value labels
			// the label text div is only inserted for transformations,
			// so we don't mess up the layout of dataset filters
			let valueLabelCode = operator == 'matches-regular-expression' ?
				'admin.import.transform.header.regularExpression' :
				'admin.import.transform.header.text'
			let valueLabel = i18n.get(valueLabelCode)
			let html = `<div class="label-text">${valueLabel}</div>`
			this.$('.value-label').prepend(html)
		}
		if (dataType === DATE || dataType === TIME || dataType === DATE_TIME) {
			this.dateFilter1 = new DatasetsDateFilterStep1(options).render()
			this.resetChooseCriteriaButton()
		}
		// must go after DatasetsTextFilter so it processes events after
		// DatasetsTextFilter (e.g. hide the Add Equation link)
		if (isGeographicOrRollup(dataType)) {
			this.rollupFilter = new DatasetsRollupFilterStep1(options).render()
			this.rollupFilter.on('change-values-operator', this.changeValuesOperator.bind(this))
			let isSingleColumn = _.includes(['is-blank', 'not-blank'], operator)
			let isThreeColumns =
				!_.includes(['is-any', 'is-not-any', 'in-another-tree', 'not-in-another-tree'], operator)
				&& !isSingleColumn
			let filterOptions = this.$('.filter-options')
			filterOptions
				.toggleClass('three-column-grid', isThreeColumns)
				.toggleClass('single-column-grid', isSingleColumn)
			// Move the filter to the correct order depending on which selection is made.
			// Order is described in #52750.
			let filterEl = this.$('.datasets-rollup-filter')
			if (_.includes(['in-another-tree', 'not-in-another-tree'], operator)) {
				filterEl.appendTo(filterOptions)
			} else {
				filterEl.prependTo(filterOptions)
			}
			this.resetChooseCriteriaButton() // #53662
		} else {
			this.$('.filter-options').removeClass('three-column-grid single-column-grid')
		}
		let hideRollupField = _.includes(['is-any', 'is-not-any'], operator)
		this.$('.datasets-rollup-filter').toggleClass('is-not-displayed',
			!isGeographicOrRollup(dataType) || hideRollupField)
		// we're not implementing calculated IF statements right now, so the
		// update type is always manual
		this.$('.update-type, .set-equation').remove()

		let isTwoSteps = twoSteps(dataType, operator)
		this.$('.choose-criteria-label', '.choose-criteria').toggleClass(
			'is-not-displayed', !isTwoSteps)
		// #30824 add label above the choose criteria button for dates
		// (this is not the same as the label ON the choose criteria button)
		if (isTwoSteps) {
			let criteriaLabel = i18n.get('common.date')
			if (operator === 'between-date-time') {
				criteriaLabel = i18n.get('common.dates')
			}
			this.$('.choose-criteria-label .label-text').html(criteriaLabel)
		}
		// if this is needed by text or numeric filters, they will show it
		this.$('[name=values], .select.control.is-any').addClass('is-not-displayed')
		this.changeValuesOperator()
		_.invoke(this.numericFilter, '_applyOperator')
		_.invoke(this.textFilter, 'changeOperator')
		_.invoke(this.rollupFilter, 'changeOperator')
		this.formView && this.formView.clearErrors()

		this.oldOperator = operator
		this.trigger('change')
	},

	/**
	 * The values rich select is now classed 'is-ignoring-change' to prevent
	 * triggering a formView change event (and updating the preview panel)
	 * before the user is actually done entering a term. (#44986)
	 * This emits a 'change:values' event for the transformation form to
	 * update the preview panel when the select has actually changed value.
	 */
	onChangeValues: function() {
		this.trigger('change:values change')
	},

	changeValuesOperator: function () {
		var operator = this.getOperator()
		let dataType = this.getDataType()
		let showingValuesSelect = isShowingValuesSelect(operator, dataType)

		this.$('.values-select').toggleClass(
			'is-not-displayed', !showingValuesSelect)

		if (showingValuesSelect) {
			let values = _.get(this.if, 'values', [])
			// remember previous selections when switching between these operators
			let oldDataType = this.getFieldDataType(this.oldFieldId)
			if (isShowingValuesSelect(this.oldOperator, oldDataType)) {
				values = this.$('[name=values]').val()
			}

			this.renderValuesSelect(values)
			// #30645 make sure values select is displayed when switching
			// between numeric and text values operators
			this.$('[name=values], .select.control.is-any').removeClass('is-not-displayed')
		}
	},

	getOperator: function() {
		return this.$('[name=operator]').val()
	},

	getDataType: function() {
		if (this.stronglyTyped) {
			return this.getFieldDataType(this.getFieldId())
		}
		// character length operators are for text, but need the numeric choices
		if (this.characterLengthOperator()) {
			return NUMBER
		}
		return ifOperators.operatorsToDataTypes[this.getOperator()]
	},

	getFieldDataType: function(fieldId) {
		const field = this.fieldsById[fieldId]
		const underlyingField = this.model.getUnderlyingField?.(field)
		return underlyingField?.dataType
	},

	getFieldId: function() {
		return this.$('[name=fieldId]').val()
	},

	characterLengthOperator: function() {
		return _.startsWith(this.getOperator(), 'character')
	},

	/**
	 * Returns whether the operator should allow text values to be
	 * entered for the values select for fields with dataType of YES_NO
	 * (other than the default values of "Yes", "No" or "Blank"
	 * as with render-yes-no-select.js)
	 * #55026 #55027
	 */
	isYesNoValuesOperator: function() {
		return _.includes(yesNoValuesOperators, this.getOperator())
	},

	chooseCriteria: function() {
		let fieldId = this.getFieldId()
		let field = this.fieldsById[fieldId]
		let addOrEdit = _.get(this.options.transformation, 'id') ? 'edit' : 'add'
		let title = i18n.get('datasets.filters.' + addOrEdit, field && field.label)

		let dataType = this.getDataType()
		let form
		if (isGeographicOrRollup(dataType)) {
			form = this.createChooseCriteriaRollupForm(title)
		} else if (isDateTimeDataType(dataType)) {
			form = this.createChooseCriteriaDateTimeForm(title)
		}
		form.on('done', () => {
			this.formView.change()
			this.chooseCriteriaButton.renderLabel(this.getCriteriaLabel())
		})

		appDisplay.dialog(form)
	},

	/**
	 * Creates the dialog launched when the user clicks on the "Choose" button
	 * for a rollup field
	 */
	createChooseCriteriaRollupForm: function(title) {
		let form = new DatasetsRollupFilterStep2({
			model: this.model,
			title,
			filter: this.readFromDom()
		})
		form.on('done', (rollupIsAnyFilters, treeItems) => {
			this.if.rollupIsAnyFilters = rollupIsAnyFilters
			let treeItemsById = _.keyBy(treeItems, 'id')
			this.treeItemsById = _.extend(this.treeItemsById, treeItemsById)
		})
		return form
	},

	/**
	 * Creates the dialog launched when the user clicks on the "Choose" button
	 * for a date, time or date/time field
	 */
	createChooseCriteriaDateTimeForm: function(title) {
		let dataType = this.getDataType()
		let constructors = {
			[DATE]: DatasetsDateFilterStep2,
			[TIME]: DatasetsTimeFilterStep2,
			[DATE_TIME]: DatasetsDateTimeFilterStep2
		}
		let Constructor = constructors[dataType]
		let form = new Constructor({
			model: this.model,
			title,
			filter: this.readFromDom(),
			allowCalculated: false,
			allowAppCalendar: true
		})
		form.on('done', filter => {
			this.if = filter
		})
		return form
	},

	// returns all the filter sub-views
	filterViews: function() {
		return [this.numericFilter, this.textFilter, this.dateFilter1,
			this.rollupFilter]
	},

	/**
	 * Toggles the visibility of the operator and criteria for
	 * If conditions that don't require it (such as "all records")
	 */
	toggleCriteriaVisibility: function () {
		let hideCriteria = this.isAllRecords()
		this.$('.filter-options').toggleClass('is-not-displayed', hideCriteria)
	},

	isAllRecords: function() {
		return this.getFieldId() === 'all-records'
	},

	hideFilterOptions: function() {
		// We're about to read data from the form. If isAllRecords is selected,
		// makes sure only that select is visible (isn't the only one when the
		// dialog is loaded #29479) before we throw it over to formHelper.readFromDom()
		if (this.isAllRecords()) {
			this.$('.filter-options').addClass('is-not-displayed')
		}
	},

	/**
	 * Returns the data that should be submitted to the server
	 */
	readFromDom: function() {
		this.hideFilterOptions()

		// this.if contains any state specified in a 2nd level dialog
		const data = _.extend({}, this.if, formHelper.readFromDom(this.el))
		// Yes/No values are case sensitive
		const dataType = this.getFieldDataType(data.fieldId)

		// #55026 #55027 don't format Yes/No values if the operator is
		// one that allows text values to be entered to the values select
		if (dataType === YES_NO && !this.isYesNoValuesOperator()) {
			data.values = _.map(data.values, value =>
				value === BLANK ? value : formatValue({ value, dataType }))
		}
		return data
	},

	test: function() {
		let criteriaRequired = i18n.get('admin.import.transform.if.criteriaRequired')
		// if the choose criteria button is visible and isn't specified
		let chooseCriteriaButton = this.$('.choose-criteria')
		let criteriaSpecified = _.trim(chooseCriteriaButton.text()) !=
			_.trim(i18n.get('common.choose'))
		if (chooseCriteriaButton.is(':visible') && !criteriaSpecified) {
			return criteriaRequired
		}

		// if the selected operator uses the values select,
		// make sure a value is specified
		let isValuesOperator = _.includes(['is-any', 'is-not-any',
			'contains-all', 'contains-any', 'does-not-contain-all',
			'does-not-contain-any', 'character-length-is-any',
			'character-length-is-not-any'], this.getOperator())
		let values = this.$('[name=values]').val() || []
		let valuesVisible = this.$('.values-select').is(':visible')
		if (isValuesOperator && _.isEmpty(values) && valuesVisible) {
			return criteriaRequired
		}

		if (isValuesOperator && this.characterLengthOperator()) {
			// only integers allowed for character length is any, is not any
			for (let i = 0; i < values.length; i++) {
				if (positiveIntegerValidator(values[i])) {
					return i18n.get('datasets.filters.value.nonNegativeNumber')
				}
			}
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([...this.filterViews(), this.operatorField,
			this.fieldField], 'remove')
	}

})

function isShowingValuesSelect(operator, dataType) {
	if (isGeographicOrRollup(dataType) &&
		_.includes(['is-any', 'is-not-any'], operator)) {
		return false
	}
	return _.includes(valuesOperators, operator)
}

export default TransformationIf
export { isShowingValuesSelect }

TransformationIf.__filename__ = 'imports/transform/transformation-if.js'
